/**
 * Shared in-memory stores for mock sub-resources.
 * Imported by the mock REST client and graphqlMockResolver (via `@/shared/api/mock`).
 * Mutations via the mock client are immediately visible to the resolver via live array references.
 */

import type { AssetModelCustomField } from "@entities/asset/types";

export interface MockMaintenanceTemplate {
  id: string;
  assetModelId: string;
  title: string;
  type: string;
  intervalMode: string;
  intervalType: string | null;
  intervalNumber: number | null;
  isActive: boolean;
  isExternal: boolean | null;
  description: string | null;
  durationHours: number | null;
  durationMinutes: number | null;
  overdueDays: number | null;
  upcomingDays: number | null;
  tasks: unknown[];
  createdAt: Date;
  updatedAt: Date;
}

export interface MockCompatiblePart {
  id: string;
  assetModelId: string;
  partId: string;
  isRecommended: boolean;
  /** When set, drives GET tags (otherwise derived from `isRecommended`). */
  tags?: string[];
  referenceQuantity?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface MockAssetModelDocument {
  id: string;
  assetModelId: string;
  fileName: string;
  fileUrl: string;
  mimeType: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface MockProblemCategoryAssignment {
  id: string;
  assetModelId: string;
  problemCategoryId: string;
  problemCategory: { id: string; name: string };
  createdAt: Date;
}

/** Custom field row in mock store (REST shape + `assetModelId` for filtering). */
export type MockAssetModelCustomFieldRow = AssetModelCustomField & {
  assetModelId: string;
};

export type { MockAssetFieldValueRow } from "./asset-field-values";
export { mockAssetFieldValues } from "./asset-field-values";

// ---------------------------------------------------------------------------
// Seed data — pre-populated for models 1, 2, 4, 6, 9
// Parts:  part-1=Heizstab OH, part-2=Heizstab UH, part-3=Rundheizung Konvektomat,
//         part-6=Türdichtung Etagenofen, part-7=Türdichtung Konvektomat,
//         part-8=Pumpendichtung Spülmaschine, part-9=Kühltheken-Glasdichtung,
//         part-10=Tiefkühler-Türdichtung, part-14=Luftfilter Kompressor,
//         part-15=Wasserfilter Spülmaschine
// Categories: icat-1=Störung, icat-2=Wartungsanfrage, icat-3=Ersatzteil, icat-4=Inspektion fällig
// ---------------------------------------------------------------------------

export const mockAssetModelMaintenanceTemplates: MockMaintenanceTemplate[] = [
  // model-1: Backofen Standard 80kW
  {
    id: "amt-1",
    assetModelId: "model-1",
    title: "Halbjahreswartung Backofen",
    type: "maintenance",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 6,
    isActive: true,
    isExternal: false,
    description: "Heizstäbe, Dichtungen und Sicherheitsventile prüfen",
    durationHours: 2,
    durationMinutes: 0,
    overdueDays: 14,
    upcomingDays: 30,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "amt-2",
    assetModelId: "model-1",
    title: "Jährliche Sicherheitsinspektion",
    type: "inspection",
    intervalMode: "fixed",
    intervalType: "years",
    intervalNumber: 1,
    isActive: true,
    isExternal: true,
    description: "Vollständige Sicherheitsinspektion durch Servicetechniker",
    durationHours: 4,
    durationMinutes: 0,
    overdueDays: 30,
    upcomingDays: 60,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-2: Backofen Pro 120kW
  {
    id: "amt-3",
    assetModelId: "model-2",
    title: "Quartalscheck Backofen Pro",
    type: "maintenance",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 3,
    isActive: true,
    isExternal: false,
    description: "Brenner, Temperaturfühler und Türdichtungen prüfen",
    durationHours: 1,
    durationMinutes: 30,
    overdueDays: 7,
    upcomingDays: 21,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-4: Konvektomat 10 Einschübe
  {
    id: "amt-4",
    assetModelId: "model-4",
    title: "Konvektomat Halbjahreswartung",
    type: "maintenance",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 6,
    isActive: true,
    isExternal: false,
    description: "Dampfsystem, Düsen und Dichtungen prüfen",
    durationHours: 2,
    durationMinutes: 30,
    overdueDays: 14,
    upcomingDays: 30,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "amt-5",
    assetModelId: "model-4",
    title: "Jahresinspektion Konvektomat",
    type: "inspection",
    intervalMode: "fixed",
    intervalType: "years",
    intervalNumber: 1,
    isActive: true,
    isExternal: true,
    description: "Drucksystem und Elektrik durch Fachbetrieb prüfen",
    durationHours: 3,
    durationMinutes: 0,
    overdueDays: 30,
    upcomingDays: 45,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-6: Kühltheke 2m
  {
    id: "amt-6",
    assetModelId: "model-6",
    title: "Kühltechnik Halbjahreswartung",
    type: "maintenance",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 6,
    isActive: true,
    isExternal: false,
    description: "Kältemittel, Kondensator und Dichtungen prüfen",
    durationHours: 1,
    durationMinutes: 0,
    overdueDays: 14,
    upcomingDays: 21,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-9: Durchschubspüler Standard
  {
    id: "amt-7",
    assetModelId: "model-9",
    title: "Quartalsservice Spülmaschine",
    type: "maintenance",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 3,
    isActive: true,
    isExternal: false,
    description: "Düsen, Dosieranlage und Wasserqualität prüfen",
    durationHours: 1,
    durationMinutes: 0,
    overdueDays: 7,
    upcomingDays: 14,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-3: Backofen Kompakt 40kW
  {
    id: "amt-8",
    assetModelId: "model-3",
    title: "Vierteljahrescheck Kompaktofen",
    type: "maintenance",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 3,
    isActive: true,
    isExternal: false,
    description: "Heizkreise, Dichtungen und Sicherheitseinrichtungen prüfen",
    durationHours: 1,
    durationMinutes: 30,
    overdueDays: 10,
    upcomingDays: 21,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "amt-9",
    assetModelId: "model-3",
    title: "Halbjahresinspektion Kompaktofen",
    type: "inspection",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 6,
    isActive: true,
    isExternal: false,
    description: "Elektrik und Temperaturführung kontrollieren",
    durationHours: 2,
    durationMinutes: 0,
    overdueDays: 14,
    upcomingDays: 30,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-5: Konvektomat 6 Einschübe
  {
    id: "amt-10",
    assetModelId: "model-5",
    title: "Quartalswartung Konvektomat 6",
    type: "maintenance",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 3,
    isActive: true,
    isExternal: false,
    description: "Dampfsystem und Türdichtungen prüfen",
    durationHours: 1,
    durationMinutes: 30,
    overdueDays: 7,
    upcomingDays: 21,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "amt-11",
    assetModelId: "model-5",
    title: "Jahresinspektion Konvektomat 6",
    type: "inspection",
    intervalMode: "fixed",
    intervalType: "years",
    intervalNumber: 1,
    isActive: true,
    isExternal: true,
    description: "Funktionsinspektion durch Fachbetrieb",
    durationHours: 2,
    durationMinutes: 30,
    overdueDays: 30,
    upcomingDays: 45,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-7: Tiefkühlschrank
  {
    id: "amt-12",
    assetModelId: "model-7",
    title: "Halbjahreswartung Tiefkühlung",
    type: "maintenance",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 6,
    isActive: true,
    isExternal: false,
    description: "Kältemittelkreislauf und Abtauung prüfen",
    durationHours: 1,
    durationMinutes: 30,
    overdueDays: 14,
    upcomingDays: 21,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "amt-13",
    assetModelId: "model-7",
    title: "Jährliche Kühlkreislauf-Inspektion",
    type: "inspection",
    intervalMode: "fixed",
    intervalType: "years",
    intervalNumber: 1,
    isActive: true,
    isExternal: true,
    description: "Dichtigkeit und Temperaturführung dokumentieren",
    durationHours: 2,
    durationMinutes: 0,
    overdueDays: 30,
    upcomingDays: 60,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-8: Kasse Touch Pro
  {
    id: "amt-14",
    assetModelId: "model-8",
    title: "Quartalsservice Kassensystem",
    type: "maintenance",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 3,
    isActive: true,
    isExternal: false,
    description: "Software-Updates, Backups und Drucker prüfen",
    durationHours: 1,
    durationMinutes: 0,
    overdueDays: 7,
    upcomingDays: 14,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "amt-15",
    assetModelId: "model-8",
    title: "Jährliche Sicherheitsinspektion POS",
    type: "inspection",
    intervalMode: "fixed",
    intervalType: "years",
    intervalNumber: 1,
    isActive: true,
    isExternal: false,
    description: "Zahlungsmodule und Netzwerk absichern",
    durationHours: 2,
    durationMinutes: 0,
    overdueDays: 30,
    upcomingDays: 45,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-10: Café-Tisch (demo: generic recurring checks)
  {
    id: "amt-16",
    assetModelId: "model-10",
    title: "Halbjährliche Sichtinspektion Möbel",
    type: "maintenance",
    intervalMode: "fixed",
    intervalType: "months",
    intervalNumber: 6,
    isActive: true,
    isExternal: false,
    description: "Stabilität, Oberfläche und Befestigungen prüfen",
    durationHours: 0,
    durationMinutes: 45,
    overdueDays: 14,
    upcomingDays: 30,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "amt-17",
    assetModelId: "model-10",
    title: "Jährliche Hygiene- und Zustandsinspektion",
    type: "inspection",
    intervalMode: "fixed",
    intervalType: "years",
    intervalNumber: 1,
    isActive: true,
    isExternal: false,
    description: "Oberflächen und Hygieneanforderungen dokumentieren",
    durationHours: 1,
    durationMinutes: 0,
    overdueDays: 30,
    upcomingDays: 60,
    tasks: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  },
];

export const mockAssetModelCompatibleParts: MockCompatiblePart[] = [
  // model-1: Backöfen — Heizstäbe + Türdichtung
  {
    id: "acp-1",
    assetModelId: "model-1",
    partId: "part-1",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-2",
    assetModelId: "model-1",
    partId: "part-2",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-3",
    assetModelId: "model-1",
    partId: "part-6",
    isRecommended: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-2: Backofen Pro — same parts
  {
    id: "acp-4",
    assetModelId: "model-2",
    partId: "part-1",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-5",
    assetModelId: "model-2",
    partId: "part-2",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-6",
    assetModelId: "model-2",
    partId: "part-6",
    isRecommended: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-4: Konvektomat — Rundheizung + Türdichtung
  {
    id: "acp-7",
    assetModelId: "model-4",
    partId: "part-3",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-8",
    assetModelId: "model-4",
    partId: "part-7",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-6: Kühltheke — Glasdichtung + Luftfilter
  {
    id: "acp-9",
    assetModelId: "model-6",
    partId: "part-9",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-10",
    assetModelId: "model-6",
    partId: "part-14",
    isRecommended: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-7: Tiefkühlschrank — Türdichtung + Luftfilter
  {
    id: "acp-11",
    assetModelId: "model-7",
    partId: "part-10",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-12",
    assetModelId: "model-7",
    partId: "part-14",
    isRecommended: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-9: Spülmaschine — Pumpendichtung + Wasserfilter
  {
    id: "acp-13",
    assetModelId: "model-9",
    partId: "part-8",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-14",
    assetModelId: "model-9",
    partId: "part-15",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-3: Kompakt-Backofen — gleiche Familie wie model-1
  {
    id: "acp-15",
    assetModelId: "model-3",
    partId: "part-1",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-16",
    assetModelId: "model-3",
    partId: "part-2",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-17",
    assetModelId: "model-3",
    partId: "part-6",
    isRecommended: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-5: Konvektomat 6 — wie model-4
  {
    id: "acp-18",
    assetModelId: "model-5",
    partId: "part-3",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-19",
    assetModelId: "model-5",
    partId: "part-7",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-7: drittes empfohlenes Ersatzteil (zusätzlich zu acp-11/12)
  {
    id: "acp-20",
    assetModelId: "model-7",
    partId: "part-9",
    isRecommended: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-8: Kassensystem — typisches Zubehör (part-50…52)
  {
    id: "acp-21",
    assetModelId: "model-8",
    partId: "part-50",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-22",
    assetModelId: "model-8",
    partId: "part-51",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-23",
    assetModelId: "model-8",
    partId: "part-52",
    isRecommended: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  // model-10: Café-Möbel — drei generische Ersatz-/Verbrauchsteile
  {
    id: "acp-24",
    assetModelId: "model-10",
    partId: "part-11",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-25",
    assetModelId: "model-10",
    partId: "part-12",
    isRecommended: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: "acp-26",
    assetModelId: "model-10",
    partId: "part-13",
    isRecommended: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
];

export const mockAssetModelDocuments: MockAssetModelDocument[] = [];

export const mockAssetModelProblemCategories: MockProblemCategoryAssignment[] = [
  // model-1: Backofen Standard
  {
    id: "apc-1",
    assetModelId: "model-1",
    problemCategoryId: "icat-1",
    problemCategory: { id: "icat-1", name: "Störung" },
    createdAt: new Date(),
  },
  {
    id: "apc-2",
    assetModelId: "model-1",
    problemCategoryId: "icat-2",
    problemCategory: { id: "icat-2", name: "Wartungsanfrage" },
    createdAt: new Date(),
  },
  // model-2: Backofen Pro
  {
    id: "apc-3",
    assetModelId: "model-2",
    problemCategoryId: "icat-1",
    problemCategory: { id: "icat-1", name: "Störung" },
    createdAt: new Date(),
  },
  {
    id: "apc-4",
    assetModelId: "model-2",
    problemCategoryId: "icat-2",
    problemCategory: { id: "icat-2", name: "Wartungsanfrage" },
    createdAt: new Date(),
  },
  // model-3: Backofen Kompakt
  {
    id: "apc-5",
    assetModelId: "model-3",
    problemCategoryId: "icat-1",
    problemCategory: { id: "icat-1", name: "Störung" },
    createdAt: new Date(),
  },
  // model-4: Konvektomat 10
  {
    id: "apc-6",
    assetModelId: "model-4",
    problemCategoryId: "icat-1",
    problemCategory: { id: "icat-1", name: "Störung" },
    createdAt: new Date(),
  },
  {
    id: "apc-7",
    assetModelId: "model-4",
    problemCategoryId: "icat-3",
    problemCategory: { id: "icat-3", name: "Ersatzteilbestellung" },
    createdAt: new Date(),
  },
  // model-5: Konvektomat 6
  {
    id: "apc-8",
    assetModelId: "model-5",
    problemCategoryId: "icat-1",
    problemCategory: { id: "icat-1", name: "Störung" },
    createdAt: new Date(),
  },
  // model-6: Kühltheke
  {
    id: "apc-9",
    assetModelId: "model-6",
    problemCategoryId: "icat-1",
    problemCategory: { id: "icat-1", name: "Störung" },
    createdAt: new Date(),
  },
  {
    id: "apc-10",
    assetModelId: "model-6",
    problemCategoryId: "icat-4",
    problemCategory: { id: "icat-4", name: "Inspektion fällig" },
    createdAt: new Date(),
  },
  // model-7: Tiefkühlschrank
  {
    id: "apc-11",
    assetModelId: "model-7",
    problemCategoryId: "icat-1",
    problemCategory: { id: "icat-1", name: "Störung" },
    createdAt: new Date(),
  },
  {
    id: "apc-12",
    assetModelId: "model-7",
    problemCategoryId: "icat-4",
    problemCategory: { id: "icat-4", name: "Inspektion fällig" },
    createdAt: new Date(),
  },
  // model-9: Spülmaschine
  {
    id: "apc-13",
    assetModelId: "model-9",
    problemCategoryId: "icat-1",
    problemCategory: { id: "icat-1", name: "Störung" },
    createdAt: new Date(),
  },
  {
    id: "apc-14",
    assetModelId: "model-9",
    problemCategoryId: "icat-3",
    problemCategory: { id: "icat-3", name: "Ersatzteilbestellung" },
    createdAt: new Date(),
  },
];

export const mockAssetModelCustomFields: MockAssetModelCustomFieldRow[] = [
  // ---------------------------------------------------------------------------
  // model-1: Backofen Standard 80kW
  // ---------------------------------------------------------------------------
  {
    id: "cf-1-1",
    assetModelId: "model-1",
    fieldLabel: "Betriebsanleitung Version",
    fieldType: "Text",
    groupName: "Dokumentation",
    dataAssignment: "model_level",
    value: "V3.2 (2023)",
    isRequired: false,
    sortOrder: 0,
  },
  {
    id: "cf-1-2",
    assetModelId: "model-1",
    fieldLabel: "IP-Schutzklasse",
    fieldType: "Text",
    groupName: "Elektrische Daten",
    dataAssignment: "model_level",
    value: "IP44",
    isRequired: false,
    sortOrder: 1,
  },
  {
    id: "cf-1-3",
    assetModelId: "model-1",
    fieldLabel: "Gewicht (kg)",
    fieldType: "Number",
    groupName: "Abmessungen",
    dataAssignment: "model_level",
    value: "320",
    isRequired: false,
    sortOrder: 2,
  },
  {
    id: "cf-1-4",
    assetModelId: "model-1",
    fieldLabel: "Inspektionsprotokoll Nr.",
    fieldType: "Text",
    groupName: "Inspektionsnachweise",
    dataAssignment: "per_asset",
    isRequired: true,
    helperText: "Nummer aus dem Erstinbetriebnahme-Protokoll",
    sortOrder: 0,
  },
  {
    id: "cf-1-5",
    assetModelId: "model-1",
    fieldLabel: "Letzte UVV-Inspektion",
    fieldType: "Date",
    groupName: "Inspektionsnachweise",
    dataAssignment: "per_asset",
    isRequired: false,
    helperText: "Datum der letzten Unfallverhütungsvorschriften-Inspektion",
    sortOrder: 1,
  },
  {
    id: "cf-1-6",
    assetModelId: "model-1",
    fieldLabel: "Standort-Notizen",
    fieldType: "Text",
    groupName: "Standort",
    dataAssignment: "per_asset",
    isRequired: false,
    helperText: "Gerätespezifische Hinweise für diesen Standort",
    sortOrder: 2,
  },
  // ---------------------------------------------------------------------------
  // model-2: Backofen Pro 120kW
  // ---------------------------------------------------------------------------
  {
    id: "cf-2-1",
    assetModelId: "model-2",
    fieldLabel: "Softwareversion",
    fieldType: "Text",
    groupName: "Steuerung",
    dataAssignment: "model_level",
    value: "FW-4.1.0",
    isRequired: false,
    sortOrder: 0,
  },
  {
    id: "cf-2-2",
    assetModelId: "model-2",
    fieldLabel: "GS-Zulassungsnummer",
    fieldType: "Text",
    groupName: "Dokumentation",
    dataAssignment: "model_level",
    value: "GS-2023-10482",
    isRequired: false,
    sortOrder: 1,
  },
  {
    id: "cf-2-3",
    assetModelId: "model-2",
    fieldLabel: "Inbetriebnahme-Protokoll Nr.",
    fieldType: "Text",
    groupName: "Inspektionsnachweise",
    dataAssignment: "per_asset",
    isRequired: true,
    helperText: "Protokollnummer aus der Erstinstallation",
    sortOrder: 0,
  },
  {
    id: "cf-2-4",
    assetModelId: "model-2",
    fieldLabel: "Letzte Kalibrierung",
    fieldType: "Date",
    groupName: "Inspektionsnachweise",
    dataAssignment: "per_asset",
    isRequired: false,
    sortOrder: 1,
  },
  // ---------------------------------------------------------------------------
  // model-4: Konvektomat 10 Einschübe
  // ---------------------------------------------------------------------------
  {
    id: "cf-4-1",
    assetModelId: "model-4",
    fieldLabel: "Softwareversion",
    fieldType: "Text",
    groupName: "Steuerung",
    dataAssignment: "model_level",
    value: "ConnectedCooking 2.8",
    isRequired: false,
    sortOrder: 0,
  },
  {
    id: "cf-4-2",
    assetModelId: "model-4",
    fieldLabel: "Geräuschpegel (dB)",
    fieldType: "Number",
    groupName: "Betriebsdaten",
    dataAssignment: "model_level",
    value: "62",
    isRequired: false,
    sortOrder: 1,
  },
  {
    id: "cf-4-3",
    assetModelId: "model-4",
    fieldLabel: "HACCP-Inspektion Datum",
    fieldType: "Date",
    groupName: "Hygienenachweise",
    dataAssignment: "per_asset",
    isRequired: true,
    helperText: "Datum der letzten HACCP-Inspektion",
    sortOrder: 0,
  },
  {
    id: "cf-4-4",
    assetModelId: "model-4",
    fieldLabel: "Kalibrierprotokoll Nr.",
    fieldType: "Text",
    groupName: "Hygienenachweise",
    dataAssignment: "per_asset",
    isRequired: false,
    helperText: "Referenz zum letzten Temperaturkalibrierprotokoll",
    sortOrder: 1,
  },
  // ---------------------------------------------------------------------------
  // model-6: Kühltheke 2m
  // ---------------------------------------------------------------------------
  {
    id: "cf-6-1",
    assetModelId: "model-6",
    fieldLabel: "Kältemittel",
    fieldType: "Text",
    groupName: "Kältedaten",
    dataAssignment: "model_level",
    value: "R290 (Propan)",
    isRequired: false,
    sortOrder: 0,
  },
  {
    id: "cf-6-2",
    assetModelId: "model-6",
    fieldLabel: "GWP-Wert",
    fieldType: "Number",
    groupName: "Kältedaten",
    dataAssignment: "model_level",
    value: "3",
    isRequired: false,
    helperText: "Global Warming Potential des Kältemittels",
    sortOrder: 1,
  },
  {
    id: "cf-6-3",
    assetModelId: "model-6",
    fieldLabel: "Kältemittelmenge (kg)",
    fieldType: "Number",
    groupName: "Kältedaten",
    dataAssignment: "per_asset",
    isRequired: true,
    helperText: "Tatsächliche Füllmenge laut Typenschild",
    sortOrder: 0,
  },
  {
    id: "cf-6-4",
    assetModelId: "model-6",
    fieldLabel: "Letzte Dichtigkeitsinspektion",
    fieldType: "Date",
    groupName: "Kältedaten",
    dataAssignment: "per_asset",
    isRequired: true,
    helperText: "Pflicht gemäß F-Gas-Verordnung",
    sortOrder: 1,
  },
  // ---------------------------------------------------------------------------
  // model-9: Durchschubspüler Standard
  // ---------------------------------------------------------------------------
  {
    id: "cf-9-1",
    assetModelId: "model-9",
    fieldLabel: "Wasserhärte-Einstellung",
    fieldType: "Text",
    groupName: "Wasseraufbereitung",
    dataAssignment: "model_level",
    value: "Stufe 3 (15–21 °dH)",
    isRequired: false,
    sortOrder: 0,
  },
  {
    id: "cf-9-2",
    assetModelId: "model-9",
    fieldLabel: "Dosiermittel Typ",
    fieldType: "Text",
    groupName: "Wasseraufbereitung",
    dataAssignment: "model_level",
    value: "Winterhalter GS 52",
    isRequired: false,
    sortOrder: 1,
  },
  {
    id: "cf-9-3",
    assetModelId: "model-9",
    fieldLabel: "Wasseranalyse Datum",
    fieldType: "Date",
    groupName: "Wasseraufbereitung",
    dataAssignment: "per_asset",
    isRequired: false,
    helperText: "Datum der letzten Wasseranalyse vor Ort",
    sortOrder: 0,
  },
  {
    id: "cf-9-4",
    assetModelId: "model-9",
    fieldLabel: "Kalk-Kontrollwert (°dH)",
    fieldType: "Number",
    groupName: "Wasseraufbereitung",
    dataAssignment: "per_asset",
    isRequired: false,
    helperText: "Gemessene Wasserhärte am Anschluss",
    sortOrder: 1,
  },
];
